import { useMemo } from 'react';
import type { MenuItemVariantDto } from '../../generated-api';
import { convertGramsToItemUnit, getUnitLabel } from '../utils/barcodeParser';
import { isWeightBasedPriceType } from '../utils/priceType';

interface ItemWeightDisplay {
  hasWeight: boolean;
  displayQuantity: number | null;
  unitLabel: string | null;
}

/**
 * Minimal order line info needed for weight display
 */
interface OrderLineForWeight {
  unitQuantity?: number;
}

/**
 * Hook to calculate and format weight display for cart items
 * Handles both weight-based items (PER_KG, PER_HG, PER_GR) and regular items (PER_ITEM, OPEN_PRICE)
 */
export function useItemWeightDisplay(
  orderLine: OrderLineForWeight | undefined,
  menuItem: MenuItemVariantDto | undefined | null
): ItemWeightDisplay {
  return useMemo(() => {
    if (!orderLine || !menuItem || !orderLine.unitQuantity || orderLine.unitQuantity === 0) {
      return {
        displayQuantity: null,
        hasWeight: false,
        unitLabel: null,
      };
    }

    const isWeightBased = isWeightBasedPriceType(menuItem.priceType);

    if (!isWeightBased) {
      return {
        displayQuantity: null,
        hasWeight: false,
        unitLabel: null,
      };
    }

    const unitLabel = getUnitLabel(menuItem.priceType);
    const displayQuantity = convertGramsToItemUnit(orderLine.unitQuantity, menuItem.priceType);

    return {
      displayQuantity,
      hasWeight: true,
      unitLabel,
    };
  }, [orderLine, menuItem]);
}
